/**
 * Layouts (multi-layout packages, ADR 0037): one graphic with a layout per aspect. The canvas is the primary layout;
 * `scene.layouts` lists the others, each a size plus per-element overrides (transform, size, anchor, visible, fontSize,
 * fit) and per-keyframe values. Fields, schema and timing are shared: only keyframe values may differ per layout.
 *
 * `sceneForLayout` flattens a layout into an ordinary single-canvas scene, so the runtime, the editor and the
 * exporter's checks never see layouts. `chooseLayout` is the host's choice rule, the contract with PRISM (its
 * `ografLayoutFor` must give the same answer for every input; test/layouts.test.ts holds the shared vectors).
 * Zod-free, like time.ts, so the runtime bundles it.
 */
import type { Element, Layout, Phase, Scene, Timeline } from './scene.ts';

type Size = { width: number; height: number };

/** A layout as a host sees it: the primary (the canvas) first, then the scene's other layouts in order. */
export interface LayoutInfo {
  id: string;
  label: string;
  width: number;
  height: number;
}

/**
 * Two aspects are the same when they differ by less than 0.5 % of the frame's: |a − b| / b < 0.005, strictly, with
 * a the layout's aspect and b the frame's (width ÷ height). This is PRISM's `sameAspect`, comparison order and all.
 */
export const SAME_ASPECT_TOLERANCE = 0.005;

/**
 * Two distances |ln(a ÷ b)| within 1e-9 of each other are a tie (the lower index wins): mirror-image aspects such as
 * 9:16 and 1:1 around 3:4 are equally near in exact arithmetic but not always in IEEE doubles. Agreed with PRISM.
 */
export const LAYOUT_TIE_EPSILON = 1e-9;

export function sameAspect(layout: Size, frame: Size): boolean {
  const a = layout.width / layout.height;
  const b = frame.width / frame.height;
  return Math.abs(a - b) / b < SAME_ASPECT_TOLERANCE;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

/** A layout's id: its reduced aspect ratio, "16x9" for 1920×1080, "4x5" for 1080×1350. */
export function layoutId(width: number, height: number): string {
  const g = gcd(width, height) || 1;
  return `${width / g}x${height / g}`;
}

const labelOf = (id: string) => id.replace('x', ':');

/** The primary layout (the canvas) followed by the scene's other layouts. */
export function layoutsOf(scene: Pick<Scene, 'canvas' | 'layouts'>): LayoutInfo[] {
  const { width, height } = scene.canvas;
  const primary = layoutId(width, height);
  return [
    { id: primary, label: labelOf(primary), width, height },
    ...(scene.layouts ?? []).map((l) => ({ id: l.id, label: l.label ?? labelOf(l.id), width: l.width, height: l.height })),
  ];
}

/**
 * The layout a host renders at `resolution` (the contract rule):
 * 1. the first layout with the same aspect (`sameAspect`, within 0.5 %);
 * 2. otherwise the nearest by the smallest |ln(layout aspect ÷ frame aspect)|, ties (within LAYOUT_TIE_EPSILON) to the
 *    lower index;
 * 3. with no usable resolution, `[0]` (the primary).
 */
export function chooseLayout<T extends Size>(layouts: readonly T[], resolution?: Size | undefined): T {
  if (layouts.length === 0) throw new Error('chooseLayout needs at least one layout');
  const r = resolution;
  if (!r || !(r.width > 0 && r.height > 0) || !Number.isFinite(r.width / r.height)) return layouts[0]!;
  const exact = layouts.find((l) => sameAspect(l, r));
  if (exact) return exact;
  const frame = r.width / r.height;
  let best = layouts[0]!;
  let bestDistance = Infinity;
  for (const l of layouts) {
    const d = Math.abs(Math.log(l.width / l.height / frame));
    if (d < bestDistance - LAYOUT_TIE_EPSILON) {
      best = l;
      bestDistance = d;
    }
  }
  return best;
}

/** Every phase of a timeline with a path for messages and track keys. */
export function timelinePhases(t: Timeline): Array<[string, Phase]> {
  const list: Array<[string, Phase]> = [
    ['timeline.in', t.in],
    ['timeline.out', t.out],
    ['timeline.update', { duration: t.update.duration, tracks: t.update.tracks }],
  ];
  t.steps.forEach((s, i) => {
    if (s.enter) list.push([`timeline.steps.${i}.enter`, s.enter]);
    if (s.loop) list.push([`timeline.steps.${i}.loop`, s.loop]);
  });
  for (const [fid, tr] of Object.entries(t.update.fields)) if (tr.mode === 'phase') list.push([`timeline.update.fields.${fid}`, tr.phase]);
  t.customActions.forEach((a, i) => list.push([`timeline.customActions.${i}.phase`, a.phase]));
  return list;
}

/** The key of a track in a layout's keyframe overrides: `<phasePath>/<element>/<property>`. */
export const trackKey = (phasePath: string, track: { element: string; property: string }): string => `${phasePath}/${track.element}/${track.property}`;

/** The key of a keyframe time in a layout's keyframe overrides ("0", "400", "333.5"). */
export const timeKey = (time: number): string => String(time);

function eachElement(elements: readonly Element[], visit: (el: Element) => void): void {
  for (const el of elements) {
    visit(el);
    if (el.type === 'group' || el.type === 'clipMask') eachElement(el.children, visit);
  }
}

/** Apply a layout's overrides to elements and keyframes in place (the scene is the caller's copy). */
function applyLayout(scene: Scene, layout: Layout): void {
  const overrides = layout.overrides ?? {};
  eachElement(scene.elements, (el) => {
    const o = overrides[el.id];
    if (!o) return;
    if (o.transform) Object.assign(el.transform, o.transform);
    if (o.size) Object.assign(el.size, o.size);
    if (o.anchor) el.anchor = { ...o.anchor };
    if (o.visible !== undefined) el.visible = o.visible;
    if (o.fontSize !== undefined && el.type === 'text') el.style.fontSize = o.fontSize;
    if (o.fit !== undefined && (el.type === 'image' || el.type === 'video')) el.fit = o.fit;
  });
  const keyframes = layout.keyframes ?? {};
  if (Object.keys(keyframes).length === 0) return;
  for (const [path, phase] of timelinePhases(scene.timeline)) {
    for (const tr of phase.tracks) {
      const values = keyframes[trackKey(path, tr)];
      if (!values) continue;
      for (const kf of tr.keyframes) {
        const v = values[timeKey(kf.time)];
        if (v !== undefined) kf.value = typeof v === 'object' ? { ...v } : v;
      }
    }
  }
}

/**
 * The scene as one ordinary single-canvas scene in layout `id`: the layout's size as the canvas, its overrides
 * applied, no `layouts`. The primary's id (or none) gives the canvas as it is. A scene without layouts is returned
 * as it is; otherwise the result is a copy and `scene` is never changed. Throws on an unknown id.
 */
export function sceneForLayout(scene: Scene, id?: string): Scene {
  if (!scene.layouts) {
    if (id !== undefined && id !== layoutId(scene.canvas.width, scene.canvas.height)) throw new Error(`no layout "${id}"`);
    return scene;
  }
  const { layouts, ...rest } = scene;
  if (id === undefined || id === layoutId(scene.canvas.width, scene.canvas.height)) return structuredClone(rest) as Scene;
  const layout = layouts.find((l) => l.id === id);
  if (!layout) throw new Error(`no layout "${id}"`);
  const flat = structuredClone(rest) as Scene;
  flat.canvas = { ...flat.canvas, width: layout.width, height: layout.height };
  applyLayout(flat, layout);
  return flat;
}
